import React, {useMemo, useState} from 'react';
import {View, Text} from 'react-native';
import {
  VictoryBar,
  VictoryChart,
  VictoryAxis,
  VictoryTheme,
  VictoryVoronoiContainer,
  VictoryLabel,
  VictoryTooltip,
} from 'victory-native';
import {useStyle} from 'src/providers/style';
import {calculateSupervisionCompliance} from 'src/common-utils/supervision-compliance';
import {useDimensions} from '@react-native-community/hooks';
import {Colors, Typography} from 'src/styles';
import {G, ForeignObject} from 'react-native-svg';
import {boxShadow} from 'src/styles/mixins';
import moment from 'moment';
import InformationTooltip from 'src/modules/programs/components/information-tooltip';
import {Separator} from 'src/common-components/atoms';

const tooltipText = `Percentage of total billable hours spent in supervision (97155).
Example: 13.5 hrs total → 1.0 supervision hr → 7% supervision`;

const HTMLFlyOut = ({x, y, datum}: any) => {
  const styles = useStyle();
  return (
    <G>
      <ForeignObject
        x={x - 70}
        y={y / 2}
        width="145"
        height="600"
        /* eslint-disable-next-line react-native/no-inline-styles */
        style={{overflow: 'visible'}}>
        <View
          style={[
            styles.borderRadius20,
            styles.backgroundColorWhite,
            styles.paddingL,
            boxShadow(Colors.RAVEN_BLACK),
          ]}>
          <Text style={[Typography.CAPTION_MEDIUM]}>Total Hours</Text>
          <Text style={[Typography.CAPTION]}>
            {Number(datum?.total) % 1 === 0
              ? Number(datum?.total).toFixed(0)
              : Number(datum?.total).toFixed(2) || ''}
          </Text>

          <Text style={[Typography.CAPTION_MEDIUM, styles.marginMTop]}>
            Supervision Hours
          </Text>
          <Text style={[Typography.CAPTION]}>
            {Number(datum?.supervision) % 1 === 0
              ? Number(datum?.supervision).toFixed(0)
              : Number(datum?.supervision).toFixed(2) || ''}
          </Text>

          <Text style={[Typography.CAPTION_MEDIUM, styles.marginMTop]}>
            Percentage
          </Text>
          <Text style={[Typography.CAPTION]}>
            {Math.floor(datum?.percentage) || ''}%
          </Text>
        </View>
      </ForeignObject>
    </G>
  );
};

const SupervisionCompliance = ({sessions}: any) => {
  const styles = useStyle();
  const dimensions = useDimensions();
  const [hoveredIndex, setHoveredIndex] = useState<any>();

  const data = useMemo(
    () => calculateSupervisionCompliance(sessions),
    [sessions],
  );

  const domain = {
    x: [0, (data.length || 1) + 1],
    y: [0, 40],
  };

  return (
    <View style={[styles.flex, styles.card, styles.padding]}>
      <View style={[styles.row]}>
        <Text style={[Typography.P2_BOLD]}>Supervision Compliance</Text>
        <Separator width={4} />
        <InformationTooltip value={tooltipText} />
      </View>

      <Text style={[Typography.P3, styles.alignSelfCenter]}>
        Weekly Supervision Compliance Rate
      </Text>
      <View style={[styles.width]}>
        <VictoryChart
          theme={VictoryTheme.material}
          width={dimensions.screen.width * 0.95}
          domain={domain}
          domainPadding={{x: 40}}
          scale={{x: 'time'}}
          containerComponent={
            <VictoryVoronoiContainer
              onActivated={points => {
                const key = points[0]?.eventKey;
                if (key !== undefined) {
                  setHoveredIndex(key);
                }
              }}
              onDeactivated={() => setHoveredIndex(null)}
            />
          }>
          <VictoryAxis
            tickFormat={t => moment(t, 'YYYY-MM-DD').format('MM/DD/YY')}
            style={{
              axis: {stroke: Colors.GRAY_100},
              tickLabels: {
                ...Typography.CAPTION,
                angle: 0,
                fill: Colors.PRIMARY_600,
              },
              grid: {stroke: 'transparent'},
            }}
            label={'Week Of'}
            axisLabelComponent={
              <VictoryLabel
                style={{...Typography.CAPTION, color: Colors.TEXT_PRIMARY}}
                dy={30}
              />
            }
            tickLabelComponent={
              <VictoryLabel
                style={{...Typography.CAPTION, fill: Colors.TEXT_PRIMARY}}
              />
            }
          />

          <VictoryAxis
            domain={{y: [0, 100]}}
            domainPadding={{x: [10, -10], y: 5}}
            tickCount={10}
            tickValues={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
            dependentAxis
            label={''}
            style={{
              axis: {stroke: 'transparent'},
            }}
            tickFormat={x => `${x}%`}
            axisLabelComponent={
              <VictoryLabel
                style={{...Typography.CAPTION, fill: Colors.TEXT_PRIMARY}}
                x={10}
              />
            }
          />

          <VictoryBar
            data={data.map((d, i) => ({...d, eventKey: i}))}
            x="week"
            y="percentage"
            barWidth={30}
            labels={({index}) => `${Math.floor(data[index]?.percentage)}%`}
            style={{
              data: {fill: '#4f46e5'},
              labels: {
                fill: Colors.PRIMARY_600,
                ...Typography.CAPTION,
              },
            }}
            labelComponent={<VictoryLabel dy={-10} />}
          />

          <VictoryBar
            data={data.map((d, i) => ({...d, eventKey: i}))}
            x="week"
            y="percentage"
            barWidth={30}
            style={{data: {fill: 'transparent'}}} // Transparent bars
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            labels={({datum, index}) =>
              String(hoveredIndex) === String(index) ? ' ' : null
            }
            labelComponent={
              <VictoryTooltip
                renderInPortal={true}
                flyoutComponent={<HTMLFlyOut />}
              />
            }
          />
        </VictoryChart>
      </View>
    </View>
  );
};

export default React.memo(SupervisionCompliance);
